import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { wsUrl } from "../lib/api";
import { useAuth } from "./AuthContext";

const RealtimeContext = createContext(null);
export const useRealtime = () => useContext(RealtimeContext);

export function RealtimeProvider({ children }) {
  const { household, loadHousehold } = useAuth();
  const queryClient = useQueryClient();
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);

  const invalidateAll = useCallback(() => {
    ["dashboard", "notifications", "expenses", "payments", "history", "settlements", "members"].forEach((k) =>
      queryClient.invalidateQueries({ queryKey: [k] })
    );
  }, [queryClient]);

  useEffect(() => {
    if (!household) return;
    let stop = false;
    let reconnectTimer = null;

    const connect = () => {
      if (stop) return;
      const ws = new WebSocket(wsUrl());
      wsRef.current = ws;

      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        if (!stop) reconnectTimer = setTimeout(connect, 3000);
      };
      ws.onerror = () => ws.close();
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "update") {
            setLastEvent(msg);
            invalidateAll();
            // Household-level changes (currency, name, admin, membership) must refresh
            // every connected roommate's household so money() re-formats live.
            if (["settings_updated", "admin_changed", "member_added", "member_removed"].includes(msg.event)) {
              loadHousehold();
            }
            toast(msg.message, { description: "Live update", icon: "⚡" });
            if ("Notification" in window && Notification.permission === "granted") {
              navigator.serviceWorker?.ready
                ?.then((reg) => reg.showNotification("RoomieSplit", { body: msg.message, tag: msg.event, icon: "/logo192.png" }))
                .catch(() => {});
            }
          }
        } catch {}
      };
    };

    connect();
    return () => {
      stop = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      wsRef.current?.close();
    };
  }, [household, invalidateAll, loadHousehold]);

  return <RealtimeContext.Provider value={{ connected, lastEvent }}>{children}</RealtimeContext.Provider>;
}
